import { useState, type FormEvent } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound } from "lucide-react";
import { ApiError } from "@/lib/api";
import { useAuthStore } from "@/store/auth";
import { BrandMark } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function ActivatePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const activate = useAuthStore((state) => state.activate);
  const status = useAuthStore((state) => state.status);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const token = params.get("token");

  if (status === "authenticated") return <Navigate to="/" replace />;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!token) return setError("This invitation link is incomplete.");
    if (password !== confirmPassword) return setError("Passwords do not match.");
    setLoading(true);
    setError(null);
    try {
      await activate(token, password);
      navigate("/", { replace: true });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Invitation could not be activated.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mobile-safe-top mobile-safe-bottom flex min-h-dvh items-center justify-center px-4 py-6">
      <form onSubmit={submit} className="w-full max-w-sm space-y-4 rounded-2xl border border-studio-border bg-studio-panel p-5 shadow-panel sm:p-6">
        <div className="flex items-start gap-3">
          <BrandMark className="h-10 w-10 shrink-0" />
          <div>
            <h1 className="text-wrap-balance text-lg font-semibold text-studio-ink">Activate your PandaFilms account</h1>
            <p className="mt-1 text-xs leading-5 text-studio-muted">Choose a permanent password to finish your invitation.</p>
          </div>
        </div>
        <label className="block text-xs font-medium text-studio-muted">New password<Input className="mt-1.5" type="password" autoComplete="new-password" minLength={8} required value={password} onChange={(event) => setPassword(event.target.value)} /></label>
        <label className="block text-xs font-medium text-studio-muted">Confirm password<Input className="mt-1.5" type="password" autoComplete="new-password" minLength={8} required value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} /></label>
        {error && <p role="alert" className="rounded-lg bg-studio-danger/10 px-3 py-2 text-xs text-studio-danger">{error}</p>}
        <Button className="w-full active:scale-[0.96] transition-transform" size="lg" type="submit" loading={loading}><KeyRound className="h-4 w-4" /> Set password and continue</Button>
      </form>
    </div>
  );
}
